import { useCallback, useMemo } from "react";
import type { TokenMetaOutput, ChainMetaOutput, Route } from "~/api/types";
import { formatAmount } from "~/lib/format";
import { ChainTokenInput } from "./ChainTokenInput";
import { SwapArrow } from "./SwapArrow";
import { SlippageControl } from "./SlippageControl";
import { ActionButton } from "./ActionButton";
import { usePreferences } from "~/hooks/usePreferences";

export interface SwapSide {
  chain: ChainMetaOutput | null;
  token: TokenMetaOutput | null;
  amount: string;
}

export interface SwapCardProps {
  from: SwapSide;
  to: SwapSide;
  onFromChange: (side: SwapSide) => void;
  onToChange: (side: SwapSide) => void;
  onSwapDirection: () => void;
  selectedRoute: Route | null;
  isStreaming: boolean;
  onExecute: () => void;
  isExecuting: boolean;
  needsRecipient?: boolean;
}

export function SwapCard({
  from,
  to,
  onFromChange,
  onToChange,
  onSwapDirection,
  selectedRoute,
  isStreaming,
  onExecute,
  isExecuting,
  needsRecipient,
}: SwapCardProps) {
  const { prefs, update } = usePreferences();

  const handleFromChainSelect = useCallback(
    (chain: ChainMetaOutput) => onFromChange({ ...from, chain, token: null }),
    [from, onFromChange],
  );
  const handleFromTokenSelect = useCallback(
    (token: TokenMetaOutput) => onFromChange({ ...from, token }),
    [from, onFromChange],
  );
  const handleFromChainTokenSelect = useCallback(
    (token: TokenMetaOutput, chain: ChainMetaOutput) =>
      onFromChange({ ...from, chain, token }),
    [from, onFromChange],
  );
  const handleFromAmountChange = useCallback(
    (amount: string) => onFromChange({ ...from, amount }),
    [from, onFromChange],
  );

  const handleToChainSelect = useCallback(
    (chain: ChainMetaOutput) => onToChange({ ...to, chain, token: null }),
    [to, onToChange],
  );
  const handleToTokenSelect = useCallback(
    (token: TokenMetaOutput) => onToChange({ ...to, token }),
    [to, onToChange],
  );
  const handleToChainTokenSelect = useCallback(
    (token: TokenMetaOutput, chain: ChainMetaOutput) =>
      onToChange({ ...to, chain, token }),
    [to, onToChange],
  );

  const toAmount = useMemo(() => {
    if (!selectedRoute || to.token?.decimals == null) return "";
    return formatAmount(selectedRoute.summary.toAmountEstimated, to.token.decimals);
  }, [selectedRoute, to.token?.decimals]);

  const isCrossChain =
    from.chain && to.chain && from.chain.chainId !== to.chain.chainId;

  return (
    <div className="w-full">
      {/* Header: Swap label + inline slippage */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-bold text-[var(--color-text-primary)]">
          {isCrossChain ? "Bridge" : "Swap"}
        </h2>
        <SlippageControl
          value={prefs.slippageBps}
          onChange={(bps) => update({ slippageBps: bps })}
        />
      </div>

      {/* Sell section */}
      <ChainTokenInput
        label="Sell"
        chain={from.chain}
        token={from.token}
        amount={from.amount}
        onChainSelect={handleFromChainSelect}
        onTokenSelect={handleFromTokenSelect}
        onChainTokenSelect={handleFromChainTokenSelect}
        onAmountChange={handleFromAmountChange}
        editable
      />

      <SwapArrow onClick={onSwapDirection} />

      {/* Buy section */}
      <ChainTokenInput
        label="Buy"
        chain={to.chain}
        token={to.token}
        amount={toAmount}
        onChainSelect={handleToChainSelect}
        onTokenSelect={handleToTokenSelect}
        onChainTokenSelect={handleToChainTokenSelect}
      />

      <div className="mt-3">
        <ActionButton
          from={from}
          to={to}
          selectedRoute={selectedRoute}
          isStreaming={isStreaming}
          isExecuting={isExecuting}
          onExecute={onExecute}
          isCrossChain={!!isCrossChain}
          needsRecipient={needsRecipient}
        />
      </div>
    </div>
  );
}
